import { useEffect, useLayoutEffect, useRef } from "react"
import { suppressNextClick } from "../../helpers/dragClick.helpers"
import type { LinearTransform } from "../../helpers/orientedResize.helpers"
import { toLocalDelta } from "../../helpers/orientedResize.helpers"
import { ancestorsTransform } from "../../helpers/selectionChrome.helpers"
import { NOT_SNAPPED, SnappedAxes } from "../../helpers/snap.helpers"
import type { SnapSession } from "../../helpers/snapSession.helpers"
import useDynamicRefs from "../useDynamicRefs"

/**
 * Click-and-hold gesture for the canvas.
 *
 * Three outcomes from one press, decided in two steps:
 *
 * 1. released without moving → nothing happens here, the `click` selects as it always did
 * 2. moved before `HOLD_DRAG_DELAY_MS` elapsed → a sloppy click; the gesture is dropped and
 *    the `click` still selects
 * 3. moved after it elapsed → drag
 *
 * Splitting "armed" from "dragging" is what keeps the delay tunable. Starting the drag when
 * the timer fires rather than when the pointer first moves would, at a short delay, flash the
 * move cursor on every ordinary click and swallow the click that was meant to select.
 *
 * `react-draggable` cannot express any of this — it starts the drag on `mousedown` and has no
 * delay — so the gesture is driven here, and `useDraggable` is left with the commit maths.
 *
 * Two things follow from owning the gesture:
 *
 * - The pointer is hit-tested with `document.elementsFromPoint`, which reports EVERY element
 *   under the point rather than just the topmost one. That is what lets a selected object be
 *   dragged while another object covers it: a plain click still goes to whatever is on top
 *   (the browser's own hit test, untouched), the hold goes to the selection.
 * - While the drag runs the node is moved with an inline `transform`, written straight to the
 *   DOM. No React state changes per pointermove, so a drag costs zero re-renders of the
 *   canvas until it is committed.
 *
 * The listener is attached to the canvas element, not to the dragged node, because a covered
 * node never receives the `pointerdown` in the first place. Only the component that owns the
 * current selection enables the hook, so exactly one listener is live at a time.
 */

/**
 * How long the pointer has to stay down before movement counts as a drag rather than as a
 * sloppy click. Keep it short — anything the user can consciously wait out reads as lag.
 */
export const HOLD_DRAG_DELAY_MS = 1

/**
 * Marks an element as a grab area for the object whose id it carries.
 *
 * The selection box spans the object AND its children, so it can reach past the object's own
 * bounds. Holding anywhere inside it used to start a drag (it lived inside the `<Draggable>`)
 * and still has to, even though the hit test now works on object ids.
 */
export const DRAG_HANDLE_ATTRIBUTE = "data-drag-handle-for"

/**
 * Marks a resize handle, so the hold gesture can keep its hands off it.
 *
 * Resizing still starts on `mousedown` (the handles only exist for the selected object and
 * carry their own cursors, so there is nothing to disambiguate). Arming a move on top of a
 * resize already in progress would resize and move the object at once.
 */
export const RESIZE_HANDLE_ATTRIBUTE = "data-resize-handle"

/**
 * Dynamic-ref key of the node a canvas object is moved by.
 *
 * Published into the registry rather than kept in a local ref because a multiselection has
 * to move objects that live in other React subtrees. Reaching them this way is what lets
 * the selected objects stay exactly where they are in the tree — and therefore keep their
 * own `z-index` — instead of being re-parented into a wrapper that floats above everything.
 */
export const dragNodeRefKey = (objectId: string): string => `drag-node-${objectId}`

/**
 * Forces the move cursor over the WHOLE document for as long as a drag runs.
 *
 * Setting `document.body.style.cursor` is not enough: every canvas object sets its own
 * `cursor`, and the nearest element under the pointer always wins. While dragging a covered
 * object the pointer sits over the covering object, so the body cursor would never show. An
 * `!important` rule on `*` is the only thing that outranks all of them.
 */
const CURSOR_OVERRIDE_STYLE_ID = "hold-drag-cursor-override"

export const setGlobalCursor = (cursor: string | null) => {
    const existing = document.getElementById(CURSOR_OVERRIDE_STYLE_ID)
    if (!cursor) {
        existing?.remove()
        return
    }
    const style = existing ?? document.createElement("style")
    style.id = CURSOR_OVERRIDE_STYLE_ID
    style.textContent = `*, *::before, *::after { cursor: ${cursor} !important; }`
    if (!existing) document.head.appendChild(style)
}

/**
 * Travel (in client px) that aborts the gesture while it is still pending. A pointer that
 * moves this far before the delay elapsed is a sloppy click, not a hold — the gesture is
 * dropped so the `click` that follows can select normally.
 */
const HOLD_DRAG_CANCEL_DISTANCE_PX = 4

/**
 * Travel (in client px) the pointer has to cover, once armed, before the drag actually
 * starts.
 *
 * Without it a one-pixel tremor between press and release counts as a drag: the click that
 * would have selected gets suppressed, and the object is nudged by that pixel. At a short
 * `HOLD_DRAG_DELAY_MS` that is most clicks, which is what made modifier-click deselect fail
 * intermittently — it depended on whether the hand was perfectly still.
 */
const DRAG_START_DISTANCE_PX = 3

export type HoldDragAxis = "x" | "y" | undefined

export type HoldDragDelta = {
    x: number
    y: number
}

type Options = {
    /** Only the component owning the current selection may listen — see the note above. */
    enabled: boolean
    /** CSS scale between client pixels and canvas units (canvas fit × zoom). */
    scale: number
    /**
     * Nodes the preview transform is written to, resolved once per drag. Their `transform`
     * must not be managed by React, or the two writers would undo each other.
     */
    getDragNodes: () => (HTMLElement | null)[]
    /**
     * Nodes hidden for as long as the drag runs, resolved once per drag.
     *
     * The selection outline is the thing the user aligns the object BY, so leaving it on
     * during a move makes lining two objects up needlessly hard — Figma drops it for the
     * same reason. Hiding beats moving the chrome along: nothing has to stay in sync, and
     * the outline simply reappears where the object landed once the store has committed.
     */
    getNodesToHide: () => (HTMLElement | null)[]
    /**
     * Does the pointer belong to this gesture? Receives every element under the pointer,
     * topmost first, including the ancestors of each hit.
     */
    isHit: (stack: Element[]) => boolean
    /** The hold elapsed; the drag is now live, even though nothing has moved yet. */
    onDragStart: () => void
    /** Shift-lock changed. Fires only on a real change, never per pointermove. */
    onAxisChange: (axis: HoldDragAxis) => void
    /**
     * Snap to objects, resolved once when the drag begins (null when it is switched off).
     * Every pointermove runs through it before the preview transform is written, so the
     * preview and the commit always agree on where the object lands.
     */
    getSnapSession: () => SnapSession | null
    /**
     * Pointer released after a live drag. `delta` is in canvas units and the canvas' own axes
     * (not turned into any parent's), axis-constrained and already snapped; `snapped` tells
     * which axes landed on another object.
     */
    onDragEnd: (delta: HoldDragDelta, axis: HoldDragAxis, snapped: SnappedAxes) => void
}

const NO_DELTA: HoldDragDelta = { x: 0, y: 0 }

const useHoldDrag = ({
    enabled,
    scale,
    getDragNodes,
    getNodesToHide,
    isHit,
    onDragStart,
    onAxisChange,
    getSnapSession,
    onDragEnd,
}: Options): void => {
    const [getRef] = useDynamicRefs<HTMLElement>()

    /**
     * The listeners below are attached once per `enabled` flip, so they must not close over
     * the props: a stale `scale` would make the object drift away from the pointer, and a
     * stale callback would commit against an outdated object. Committed before paint, so an
     * input event can never observe the previous render's values.
     */
    const latest = useRef({
        scale,
        getDragNodes,
        getNodesToHide,
        isHit,
        onDragStart,
        onAxisChange,
        getSnapSession,
        onDragEnd,
    })
    useLayoutEffect(() => {
        latest.current = {
            scale,
            getDragNodes,
            getNodesToHide,
            isHit,
            onDragStart,
            onAxisChange,
            getSnapSession,
            onDragEnd,
        }
    })

    useEffect(() => {
        if (!enabled) return

        const canvasElement = getRef("canvas")?.current
        if (!canvasElement) return

        let pointerId: number | null = null
        let startX = 0
        let startY = 0
        let holdTimer: ReturnType<typeof setTimeout> | undefined
        /** the hold elapsed — the pointer MAY now drag, but has not moved yet */
        let armed = false
        /** the pointer has actually moved while armed — a drag is live */
        let dragging = false
        let axis: HoldDragAxis = undefined
        let delta: HoldDragDelta = NO_DELTA
        let snapped: SnappedAxes = NOT_SNAPPED
        let snapSession: SnapSession | null = null

        /** both resolved once the drag actually begins, so a remount before that cannot go stale */
        let dragNodes: HTMLElement[] = []
        let hiddenNodes: HTMLElement[] = []
        /**
         * What each drag node's parent objects do to it (rotation, scale, skew), measured with
         * the nodes. A node inside a turned parent is translated in that parent's axes, so the
         * pointer's travel has to be turned back into them first, or the object would wander
         * off at the parent's angle.
         */
        let dragNodeTransforms: LinearTransform[] = []

        const applyTransform = (value: string) => {
            dragNodes.forEach((node) => {
                node.style.transform = value
            })
        }

        /** The preview: every node moved by `delta`, as the canvas sees it. */
        const applyDelta = (canvasDelta: HoldDragDelta) => {
            dragNodes.forEach((node, index) => {
                const local = toLocalDelta(dragNodeTransforms[index], canvasDelta)
                node.style.transform = `translate(${local.x}px, ${local.y}px)`
            })
        }

        /** `visibility`, not `display`: no layout is invalidated on the way in or out */
        const applyChromeVisibility = (value: "hidden" | "") => {
            hiddenNodes.forEach((node) => {
                node.style.visibility = value
            })
        }

        /**
         * `react-draggable` used to do this for us (its `enableUserSelectHack`). Without it a
         * drag paints a text selection across the whole canvas.
         */
        const lockDocument = () => {
            document.body.style.userSelect = "none"
            setGlobalCursor("move")
        }
        const unlockDocument = () => {
            document.body.style.userSelect = ""
            setGlobalCursor(null)
        }

        const stopTracking = () => {
            if (holdTimer !== undefined) {
                clearTimeout(holdTimer)
                holdTimer = undefined
            }
            window.removeEventListener("pointermove", handlePointerMove)
            window.removeEventListener("pointerup", handlePointerUp)
            window.removeEventListener("pointercancel", handlePointerCancel)
            pointerId = null
            armed = false
            dragging = false
            axis = undefined
            delta = NO_DELTA
            snapped = NOT_SNAPPED
            // takes the guides down, whichever way the drag ended
            snapSession?.end()
            snapSession = null
            dragNodes = []
            dragNodeTransforms = []
            hiddenNodes = []
        }

        /** Undo everything a live drag changed outside React, without committing it. */
        const abortDrag = () => {
            if (!dragging) return
            applyTransform("")
            applyChromeVisibility("")
            unlockDocument()
        }

        /**
         * The drag only becomes visible once the pointer MOVES while armed, not the moment
         * the hold elapses.
         *
         * With a short delay the timer fires during every ordinary click, and starting there
         * would flash the move cursor, hide the selection outline and re-render the chrome on
         * each one — and, worse, the click suppression on release would swallow the click
         * that was supposed to select (or shift-click-deselect) the object.
         */
        const isElement = (node: HTMLElement | null): node is HTMLElement => node !== null

        const beginDrag = () => {
            dragging = true
            dragNodes = latest.current.getDragNodes().filter(isElement)
            dragNodeTransforms = dragNodes.map((node) => ancestorsTransform(node, canvasElement))
            hiddenNodes = latest.current.getNodesToHide().filter(isElement)
            // measured before the first transform, while everything is still where it rests
            snapSession = latest.current.getSnapSession()
            applyChromeVisibility("hidden")
            lockDocument()
            // text the press happened to select would otherwise stay highlighted
            window.getSelection()?.removeAllRanges()
            latest.current.onDragStart()
        }

        function handlePointerMove(event: PointerEvent) {
            if (event.pointerId !== pointerId) return

            const rawX = event.clientX - startX
            const rawY = event.clientY - startY

            if (!dragging) {
                if (!armed) {
                    // a pointer that travels before the hold elapsed is a sloppy click, not a
                    // drag — drop the gesture so the click that follows can select normally
                    if (
                        Math.abs(rawX) > HOLD_DRAG_CANCEL_DISTANCE_PX ||
                        Math.abs(rawY) > HOLD_DRAG_CANCEL_DISTANCE_PX
                    ) {
                        stopTracking()
                    }
                    return
                }
                // armed, but the pointer still has to mean it — see DRAG_START_DISTANCE_PX
                if (
                    Math.abs(rawX) < DRAG_START_DISTANCE_PX &&
                    Math.abs(rawY) < DRAG_START_DISTANCE_PX
                ) {
                    return
                }
                beginDrag()
            }

            // the canvas is measured in a layout effect, so the very first frames can report
            // a scale of 0 — dividing by it would send the object off to Infinity
            const scale = latest.current.scale || 1
            const scaledX = rawX / scale
            const scaledY = rawY / scale

            // Shift locks the drag to the dominant axis and keeps it there until released —
            // what react-draggable's `axis` prop used to provide.
            const nextAxis: HoldDragAxis = event.shiftKey
                ? axis ?? (Math.abs(scaledX) > Math.abs(scaledY) ? "x" : "y")
                : undefined

            if (nextAxis !== axis) {
                axis = nextAxis
                latest.current.onAxisChange(axis)
            }

            const pointerDelta = {
                x: axis === "y" ? 0 : scaledX,
                y: axis === "x" ? 0 : scaledY,
            }
            // ctrl suspends snapping for as long as it is held, as in Figma
            const result = snapSession?.snap(pointerDelta, axis, event.ctrlKey)
            delta = result?.delta ?? pointerDelta
            snapped = result?.snapped ?? NOT_SNAPPED
            applyDelta(delta)
        }

        function handlePointerUp(event: PointerEvent) {
            if (event.pointerId !== pointerId) return

            if (!dragging) {
                // pressed and released without ever moving: a plain click. Nothing to undo,
                // and crucially no click suppression — the click has to reach the object.
                stopTracking()
                return
            }

            const committedDelta = delta
            const committedAxis = axis
            const committedSnapped = snapped

            // Drop the preview transform, bring the outline back and commit, all in the same
            // handler. React re-measures the chrome in a layout effect of that same commit,
            // so the outline never gets painted at the position the object just left.
            applyTransform("")
            applyChromeVisibility("")
            unlockDocument()
            stopTracking()

            // The browser still fires a `click` after this, and rarely on the object that
            // was dragged — see the note in dragClick.helpers. Letting it through would move
            // the selection off the object the user has just placed.
            suppressNextClick()
            latest.current.onDragEnd(committedDelta, committedAxis, committedSnapped)
        }

        function handlePointerCancel(event: PointerEvent) {
            if (event.pointerId !== pointerId) return
            abortDrag()
            stopTracking()
        }

        const handlePointerDown = (event: PointerEvent) => {
            // secondary buttons belong to the context menu; a gesture in flight keeps the pointer
            if (event.button !== 0 || pointerId !== null) return
            /**
             * On macOS ctrl+click IS the secondary click, so it also opens the context
             * menu — arming a drag on it would do both at once.
             *
             * Shift deliberately does NOT bail, even though it is now the add-to-selection
             * modifier: shift+drag still constrains the move to one axis. The two are told
             * apart by movement alone, which is what `DRAG_START_DISTANCE_PX` is for — a
             * shift+click that never travels that far stays a click and reaches the object.
             */
            if (event.ctrlKey || event.metaKey) return

            const stack = document.elementsFromPoint(event.clientX, event.clientY)
            if (stack.some((element) => element.hasAttribute(RESIZE_HANDLE_ATTRIBUTE))) return
            if (!latest.current.isHit(stack)) return

            pointerId = event.pointerId
            startX = event.clientX
            startY = event.clientY

            // on window, not on the canvas: a drag that leaves the canvas must keep tracking
            window.addEventListener("pointermove", handlePointerMove)
            window.addEventListener("pointerup", handlePointerUp)
            window.addEventListener("pointercancel", handlePointerCancel)
            holdTimer = setTimeout(() => {
                holdTimer = undefined
                armed = true
            }, HOLD_DRAG_DELAY_MS)
        }

        canvasElement.addEventListener("pointerdown", handlePointerDown)

        return () => {
            canvasElement.removeEventListener("pointerdown", handlePointerDown)
            // unmounting (or being disabled) mid-drag must not leave the document locked or
            // the node stuck under a transform that nothing will ever clear
            abortDrag()
            stopTracking()
        }
    }, [enabled, getRef])
}

export default useHoldDrag
